import React, { useState, useEffect, useMemo } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const POSITIONS = ['ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

const POS_COLOR = {
  QB:  'var(--accent2)',
  RB:  '#00d4aa',
  WR:  '#4fc3f7',
  TE:  '#ce93d8',
  K:   '#ffd32a',
  DEF: '#8b90a0',
};

function PosBadge({ pos }) {
  const color = POS_COLOR[pos] || 'var(--text3)';
  return (
    <span style={{
      background: `${color}18`,
      color,
      border: `1px solid ${color}`,
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 700,
      padding: '2px 7px',
      letterSpacing: 0.8,
      fontFamily: 'var(--font-mono)',
    }}>
      {pos}
    </span>
  );
}

function SortIcon({ col, sortKey, sortDir }) {
  if (sortKey !== col) return <span style={{ color: 'var(--text3)', marginLeft: 4 }}>⇅</span>;
  return <span style={{ color: 'var(--accent)', marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

export default function ADPPage({ api }) {
  const base = api || API;
  const [players, setPlayers] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [posFilter, setPosFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('rank');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    setLoading(true);
    fetch(`${base}/api/adp`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        setPlayers(data.players || []);
        setFetchedAt(data.fetched_at || null);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, [base]);

  const handleSort = (col) => {
    if (sortKey === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(col);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let list = players;
    if (posFilter !== 'ALL') list = list.filter(p => p.position === posFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(p =>
        (p.player_name || '').toLowerCase().includes(q) ||
        (p.team || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (av == null) av = sortDir === 'asc' ? Infinity : -Infinity;
      if (bv == null) bv = sortDir === 'asc' ? Infinity : -Infinity;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [players, posFilter, search, sortKey, sortDir]);

  const fmtDate = (iso) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const thStyle = (col) => ({
    padding: '10px 14px',
    textAlign: col === 'rank' || col === 'adp' || col === 'bye_week' ? 'center' : 'left',
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    fontWeight: 700,
    color: sortKey === col ? 'var(--accent)' : 'var(--text3)',
    letterSpacing: 0.8,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--border)',
  });

  const tdStyle = (align = 'left') => ({
    padding: '9px 14px',
    fontSize: 13,
    color: 'var(--text)',
    textAlign: align,
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    whiteSpace: 'nowrap',
  });

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80, color: 'var(--text3)' }}>
      Loading ADP data…
    </div>
  );

  if (error) return (
    <div style={{ textAlign: 'center', padding: 80, color: 'var(--red)' }}>
      Failed to load ADP: {error}
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: 'var(--text)' }}>
            2026 PPR ADP
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
            Sleeper consensus · {players.length} players · Updated {fmtDate(fetchedAt)}
          </div>
        </div>
        {/* Search */}
        <input
          type="text"
          placeholder="Search player or team…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '7px 12px',
            color: 'var(--text)',
            fontSize: 13,
            width: 220,
            outline: 'none',
          }}
        />
      </div>

      {/* Position filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {POSITIONS.map(pos => {
          const active = posFilter === pos;
          const color = POS_COLOR[pos] || 'var(--accent)';
          return (
            <button
              key={pos}
              onClick={() => setPosFilter(pos)}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                border: `1px solid ${active ? color : 'var(--border)'}`,
                background: active ? `${color}22` : 'transparent',
                color: active ? color : 'var(--text3)',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                cursor: 'pointer',
                letterSpacing: 0.6,
                transition: 'all 0.15s',
              }}
            >
              {pos}
            </button>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }}>
          {filtered.length} players
        </span>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg3)' }}>
                <th style={thStyle('rank')} onClick={() => handleSort('rank')}>
                  # <SortIcon col="rank" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th style={thStyle('player_name')} onClick={() => handleSort('player_name')}>
                  Player <SortIcon col="player_name" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th style={thStyle('team')} onClick={() => handleSort('team')}>
                  Team <SortIcon col="team" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th style={thStyle('position')} onClick={() => handleSort('position')}>
                  Pos <SortIcon col="position" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th style={thStyle('adp')} onClick={() => handleSort('adp')}>
                  ADP <SortIcon col="adp" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th style={thStyle('pos_rank')} onClick={() => handleSort('pos_rank')}>
                  Pos Rank <SortIcon col="pos_rank" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th style={thStyle('bye_week')} onClick={() => handleSort('bye_week')}>
                  Bye <SortIcon col="bye_week" sortKey={sortKey} sortDir={sortDir} />
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ ...tdStyle('center'), padding: 40, color: 'var(--text3)' }}>
                    No players match your filters.
                  </td>
                </tr>
              ) : filtered.map((p, i) => (
                <tr
                  key={p.player_id ?? i}
                  style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}
                >
                  <td style={{ ...tdStyle('center'), fontFamily: 'var(--font-mono)', color: 'var(--text3)', fontSize: 12, width: 44 }}>
                    {p.rank}
                  </td>
                  <td style={{ ...tdStyle('left'), fontWeight: 600 }}>
                    {p.player_name}
                  </td>
                  <td style={{ ...tdStyle('left'), fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text3)' }}>
                    {p.team || '—'}
                  </td>
                  <td style={tdStyle('left')}>
                    <PosBadge pos={p.position} />
                  </td>
                  <td style={{ ...tdStyle('center'), fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)' }}>
                    {p.adp ?? '—'}
                  </td>
                  <td style={{ ...tdStyle('left'), fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {p.pos_rank || '—'}
                  </td>
                  <td style={{ ...tdStyle('center'), fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text3)' }}>
                    {p.bye_week ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text3)', textAlign: 'right' }}>
        Source: Sleeper PPR ADP · Refreshes every 24 hours
      </div>
    </div>
  );
}
